/**
 * Horizontal freeze and price-block separators for the price-list tables.
 *
 * Freeze runs through U.M. Vuetify sticks those columns and computes the
 * offset from each column's width, so hiding a column keeps the rest aligned.
 * Separators sit on the last visible column of a block, not on a fixed index.
 */

export const FREEZE_THROUGH_KEY = 'measurement_unit';

/** Pixel widths. Vuetify only offsets fixed columns when width is numeric. */
export const FREEZE_COLUMN_WIDTHS = {
    select: 104,
    serial_number: 84,
    visible_in_offer_app: 96,
    furnizor: 140,
    product_code: 132,
    product_name: 180,
    status: 108,
    is_default: 124,
    categories: 120,
    measurement_unit: 64,
    rowActions: 168,
};

/**
 * Lista în lucru blocks that end with a vertical rule.
 * Order is the on-screen order.
 */
export const DRAFT_PRICE_GROUPS = [
    [
        'supplier_list_price_old',
        'supplier_list_price_new',
        'supplier_list_price_adjustment_ratio',
    ],
    [
        'entry_price_ron_inc_vat_old',
        'entry_price_ron_inc_vat',
        'entry_price_adjustment_ratio',
    ],
    [
        'markup_reference',
        'markup_actual_old',
        'markup_actual',
        'markup_actual_adjustment_ratio',
    ],
    [
        'previous_price_ex_vat',
        'price_ex_vat',
        'previous_price',
        'price',
        'price_adjustment_ratio',
    ],
];

export const DRAFT_PRICE_BLOCKS = [
    {
        id: 'supplier',
        label: 'Preț listă',
        shortLabel: 'Listă',
        keys: DRAFT_PRICE_GROUPS[0],
        deltaKey: 'supplier_list_price_adjustment_ratio',
        oldKey: 'supplier_list_price_old',
        newKey: 'supplier_list_price_new',
        valueKind: 'currency',
    },
    {
        id: 'entry',
        label: 'Achiziție',
        shortLabel: 'Achiziție',
        keys: DRAFT_PRICE_GROUPS[1],
        deltaKey: 'entry_price_adjustment_ratio',
        oldKey: 'entry_price_ron_inc_vat_old',
        newKey: 'entry_price_ron_inc_vat',
        valueKind: 'ron',
    },
    {
        id: 'markup',
        label: 'Adaos',
        shortLabel: 'Adaos',
        keys: DRAFT_PRICE_GROUPS[2],
        deltaKey: 'markup_actual_adjustment_ratio',
        oldKey: 'markup_actual_old',
        newKey: 'markup_actual',
        valueKind: 'percent',
    },
    {
        id: 'sale',
        label: 'Vânzare',
        shortLabel: 'Vânzare',
        keys: DRAFT_PRICE_GROUPS[3],
        deltaKey: 'price_adjustment_ratio',
        oldKey: 'previous_price',
        newKey: 'price',
        valueKind: 'ron',
    },
];

const GROUP_CLASS_BY_ID = {
    supplier: 'col-group-supplier',
    entry: 'col-group-entry',
    markup: 'col-group-markup',
    sale: 'col-group-sale',
};

/**
 * Keep identity columns, expand one price block, and collapse the others
 * to a single difference pill. `blockId` null leaves the full table.
 *
 * @param {Object[]} headers
 * @param {string|null} blockId
 * @returns {Object[]}
 */
export function headersForPriceBlock(headers, blockId) {
    const list = Array.isArray(headers) ? headers : [];
    const active = DRAFT_PRICE_BLOCKS.find((block) => block.id === blockId);
    if (!active) return list;
    const grouped = new Set(DRAFT_PRICE_BLOCKS.flatMap((block) => block.keys));
    const byKey = new Map(list.map((header) => [header?.key, header]));
    const before = [];
    const after = [];
    let seenGroup = false;
    list.forEach((header) => {
        if (!header?.key || grouped.has(header.key)) {
            if (header?.key && grouped.has(header.key)) seenGroup = true;
            return;
        }
        if (!seenGroup) before.push(header);
        else after.push(header);
    });
    const middle = [];
    DRAFT_PRICE_BLOCKS.forEach((block) => {
        const visibleKeys = block.keys.filter((key) => byKey.has(key));
        if (!visibleKeys.length) return;
        if (block.id === active.id) {
            visibleKeys.forEach((key) => middle.push(byKey.get(key)));
            return;
        }
        const groupClass = GROUP_CLASS_BY_ID[block.id];
        middle.push({
            title: block.shortLabel,
            key: `block_delta_${block.id}`,
            align: 'center',
            sortable: false,
            width: 96,
            headerProps: { class: `header-wrap ${groupClass} col-group-end` },
            cellProps: { class: `${groupClass} col-group-end block-delta-cell` },
        });
    });
    return [...before, ...middle, ...after];
}

const GROUP_CLASS_NAMES = [
    'col-group-supplier',
    'col-group-entry',
    'col-group-markup',
    'col-group-sale',
];

const WRAP_KEYS = new Set(['product_name', 'categories']);

function joinClass(existing, extra) {
    const parts = [];
    if (Array.isArray(existing)) {
        existing.forEach((name) => {
            if (name) parts.push(String(name));
        });
    } else if (typeof existing === 'string' && existing.trim()) {
        parts.push(existing.trim());
    }
    const seen = new Set(parts.join(' ').split(/\s+/).filter(Boolean));
    extra.forEach((name) => {
        if (name && !seen.has(name)) {
            parts.push(name);
            seen.add(name);
        }
    });
    return parts.join(' ');
}

function withClasses(header, classNames) {
    if (!classNames.length) return header;
    return {
        ...header,
        headerProps: {
            ...(header.headerProps || {}),
            class: joinClass(header.headerProps?.class, classNames),
        },
        cellProps: {
            ...(header.cellProps || {}),
            class: joinClass(header.cellProps?.class, classNames),
        },
    };
}

function freezeWidth(header) {
    const declared = Number(header?.width);
    if (Number.isFinite(declared) && declared > 0) return declared;
    return FREEZE_COLUMN_WIDTHS[header?.key] || 96;
}

/**
 * @param {Object[]} headers Visible headers, in display order.
 * @param {{
 *   freezeThroughKey?: string,
 *   groups?: string[][],
 *   pinEndKeys?: string[],
 *   pinColumns?: boolean,
 * }} [options]
 * @returns {Object[]}
 */
export function applyTableChrome(headers, options = {}) {
    const list = Array.isArray(headers) ? headers : [];
    const freezeThroughKey = options.freezeThroughKey || FREEZE_THROUGH_KEY;
    const groups = Array.isArray(options.groups) ? options.groups : [];
    const pinEndKeys = new Set(options.pinEndKeys || []);
    // Sticky columns are wider than a phone or tablet. Pin only on desktop.
    const pinColumns = options.pinColumns !== false;

    const groupClassByKey = new Map();
    const groupEndKeys = new Set();
    groups.forEach((keys, index) => {
        const className = GROUP_CLASS_NAMES[index] || `col-group-${index}`;
        const keySet = new Set(keys);
        let lastKey = null;
        list.forEach((header) => {
            if (!keySet.has(header?.key)) return;
            groupClassByKey.set(header.key, className);
            lastKey = header.key;
        });
        if (lastKey) groupEndKeys.add(lastKey);
    });

    const freezeIndex = list.findIndex((header) => header?.key === freezeThroughKey);
    let identityEndIndex = freezeIndex;
    if (identityEndIndex < 0) {
        identityEndIndex = -1;
        list.forEach((header, index) => {
            if (!header?.key) return;
            if (groupClassByKey.has(header.key) || pinEndKeys.has(header.key)) return;
            if (identityEndIndex === -1 || index === identityEndIndex + 1) {
                identityEndIndex = index;
            }
        });
    }
    if (identityEndIndex >= 0 && list[identityEndIndex]?.key) {
        groupEndKeys.add(list[identityEndIndex].key);
    }

    return list.map((header, index) => {
        if (!header || !header.key) return header;
        const next = { ...header };
        const classes = [];
        const inIdentity = identityEndIndex >= 0
            && index <= identityEndIndex
            && !groupClassByKey.has(header.key)
            && !pinEndKeys.has(header.key);
        if (pinColumns && inIdentity) {
            next.fixed = 'start';
            const width = freezeWidth(header);
            if (width) {
                next.width = width;
                next.minWidth = width;
                next.maxWidth = width;
            }
        }
        if (pinColumns && pinEndKeys.has(header.key)) {
            next.fixed = 'end';
            const width = freezeWidth(header) || FREEZE_COLUMN_WIDTHS.rowActions;
            next.width = width;
            next.minWidth = width;
            next.maxWidth = width;
        }
        const groupClass = groupClassByKey.get(header.key);
        if (groupClass) classes.push(groupClass);
        if (groupEndKeys.has(header.key)) classes.push('col-group-end');
        if (header.key === 'product_name') classes.push('col-freeze-name');
        if (WRAP_KEYS.has(header.key)) classes.push('col-freeze-wrap');
        return withClasses(next, classes);
    });
}
